import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { alpha, styled } from '@mui/material/styles';
import {
  Alert,
  Box,
  GlobalStyles,
  Link,
  Stack,
  Typography
} from '@mui/material';
// components
import Page from 'src/components/Page';
import Icon from 'src/components/universe/Icon';
import BrandBanner, { SCENE_FRAME } from 'src/components/universe/BrandBanner';
import LegalLine from 'src/components/universe/LegalLine';
// universe
import { LAYER_FONT_STYLES, PAGE_FONT_SX } from 'src/theme/universe/pageFont';
import {
  MOTION,
  RADIUS,
  UNIVERSE,
  UNIVERSE_FONT
} from 'src/theme/universe/brand';
// a return from Microsoft carries its error the way a dead email link does;
// JWTContext reads that one, so this shell must not read it twice
import { SSO_RETURN } from 'src/utils/ssoAttempt';
import { AUTH_PORTAL_LIST, AUTH_SHORT, AUTH_SHORTER } from './portals';
import AuthPortalMenu from './AuthPortalMenu';

// ----------------------------------------------------------------------
// Shell for every auth screen: sign in, forgot password, sign-in link, set a
// new password, register. Each of those is its own page with one job, so the
// shell only has to frame a title, a short line of context, and the form.
//
// Left: the brand surface of DESIGN.md §2 — the Universe night with the
// planet's limb burning light blue, which is where the blue of the shell on
// the other side of the form is explained. Right: theme surfaces, so the
// form is readable in both light and dark mode.
//
// The three portals share one identity (§28 DECISION 3) and differ by the
// headline and the copy they pass through.
//
// SECOND PASS, 2026-10-08 (the owner, on both halves: "improve it big times",
// "i don't like this neither"). The sky got its own pass in `BrandBanner`;
// this side got the card the form is written on, and the way to the other two
// portals under it.
//
// THIRD PASS, 2026-10-10 (the owner: "i don't like design of login page").
// What was left wrong was the SHAPE of the page, not either half of it. The
// panel was capped at 860px, so a 1920 window was 45% night, 55% empty pale
// canvas, and a 456px card adrift in the middle of the empty part. Capping
// the content and not the window is the same fault the portal chooser had
// the same day.
//
// It is one scene now: the Universe night takes the WHOLE window
// (`BrandBanner variant="scene"`), the planet's horizon runs across the foot
// of it, and the form card is laid over it as the only lit surface on the
// page. From 1200 up the brand copy holds the left of the frame and the card
// the right; below that the copy stands down — a headline squeezed into the
// 200px left over beside a card is worse than no headline — and the card
// centres on the scene with the lock-up above it.
//
// Everything outside the card is therefore ON THE NIGHT, and painted for it:
// the back link, the portal switch and their focus rings take explicit white
// values rather than `text.secondary` and the theme accent, which are a dark
// grey and a navy in light mode and would be invisible.
//
// FOURTH PASS, 2026-10-11 (the owner: the card "feels too short and
// vertically compressed … taller, better balanced, and more spacious"). The
// card was 520 × 419 at 1536 — wider than tall, with 24px between the title
// and the form and the fields packed under it. It is a PORTRAIT sheet now:
// 472 wide, 48px of paper round the content, 32px under the heading, every
// control 44px (`AUTH_CONTROL`). On a short window — a 13" laptop's 720px less
// the browser's own chrome (`AUTH_SHORT`), a 1280 × 600 window or a phone with
// its bars showing (`AUTH_SHORTER`) — the paper and the gaps step down, a
// notch at a time, so the taller card still fits without the page scrolling.
// Measured at twelve windows from 1920 × 947 to 360 × 740.
//
// WHILE THE SESSION IS RESOLVED (2026-10-11), `pending`. The guards used to
// render the sign-in form until the account came back, so a person back from
// Microsoft saw the form they had just got past for about a second. They wait
// now, on this: the same sky, and one line in the card's place. What belongs
// to the card — the way back, the other portals, the legal line under it —
// comes with the card; what belongs to the sky stays, so the form that does
// follow (a refused login, an expired session) lands on a scene that has not
// moved.
// ----------------------------------------------------------------------

const RootStyle = styled('div')({
  position: 'relative',
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: UNIVERSE.night,
  overflow: 'hidden'
});

// `width` lets the registration flow breathe (it carries a password checklist
// and the terms step) without needing a second shell that would drift from this
// one — which is exactly how the old OnboardingShell ended up unrecognisable
// next to the login screen.
const ContentStyle = styled('div', {
  shouldForwardProp: (prop) => prop !== 'width'
})(({ theme, width }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: width || 472,
  // Centred on the scene until there is room for the brand copy beside it.
  marginLeft: 'auto',
  marginRight: 'auto',
  [theme.breakpoints.up('lg')]: { marginRight: 0 }
}));

/**
 * The sheet the form is written on — and now the only lit surface in the
 * window, so it casts a real shadow on the night instead of the hairline
 * lift a card gets on paper. In dark mode the paper is close enough to the
 * night to need an edge as well.
 */
const cardSx = (theme) => ({
  position: 'relative',
  px: { xs: 3, sm: 6 },
  pt: { xs: 3, sm: 6 },
  pb: { xs: 3, sm: 5.5 },
  [AUTH_SHORT]: { px: 5, pt: 4.5, pb: 4 },
  [AUTH_SHORTER]: { px: { xs: 3, sm: 4.5 }, pt: 3.5, pb: 3 },
  bgcolor: 'background.paper',
  border:
    theme.palette.mode === 'light'
      ? 'none'
      : `1px solid ${alpha('#FFFFFF', 0.1)}`,
  borderRadius: `${RADIUS.modal}px`,
  boxShadow:
    theme.palette.mode === 'light'
      ? '0 44px 88px -30px rgba(0,2,14,.74), 0 10px 28px -14px rgba(0,2,14,.5)'
      : '0 44px 88px -30px rgba(0,0,0,.92), inset 0 1px 0 rgba(255,255,255,.05)'
});

/** A focus ring that is visible on the night, where the accent is not. */
const nightFocusSx = {
  outline: 'none',
  boxShadow: `0 0 0 2px ${alpha('#FFFFFF', 0.9)}`
};

// ----------------------------------------------------------------------

AuthPortalLayout.propTypes = {
  portal: PropTypes.object.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  /** Renders a "← back" link above the title, for the secondary flows. */
  backTo: PropTypes.string,
  backLabel: PropTypes.string,
  /** Widen the form column — the registration flow needs more room. */
  contentWidth: PropTypes.number,
  /**
   * The session is still being resolved (a return from Microsoft, an email
   * link's tokens, a stored session): this line holds the card's place. The
   * guards set it; a page never does.
   */
  pending: PropTypes.string,
  children: PropTypes.node
};

// ----------------------------------------------------------------------
// Feedback for the links we send by email.
//
// When a confirmation, magic or recovery link is stale, already used, or aimed
// at an address Supabase will not redirect to, GoTrue does not fail loudly — it
// bounces the browser back with ?error=...&error_description=... and nothing
// else. Every auth page sits inside this shell, so reading that once here means
// a dead link says so instead of silently rendering an ordinary sign-in form,
// which is what "the confirm link shows no success or error" looked like.
// ----------------------------------------------------------------------

function useEmailLinkError(enabled = true) {
  const [message, setMessage] = useState('');

  useEffect(() => {
    // the way back from Microsoft is not an email link: JWTContext reads its
    // error and the sign-in form says what happened in its own words
    if (SSO_RETURN) return;
    // a pending shell is not the page that will say it: reading the error
    // here would clean it out of the address bar before that page mounts
    if (!enabled) return;
    const { search, hash } = window.location;
    const query = new URLSearchParams(search);
    // GoTrue uses the query for PKCE and the hash for the implicit flow.
    const fragment = new URLSearchParams((hash || '').replace(/^#/, ''));
    const description =
      query.get('error_description') || fragment.get('error_description');
    const code = query.get('error_code') || fragment.get('error_code');
    if (!description && !code) return;

    const readable = (description || code || '').replace(/\+/g, ' ');
    setMessage(
      /expired|invalid/i.test(readable)
        ? 'That link is no longer valid — it may have expired or already been used. Request a new one below.'
        : readable
    );

    // Leave the address bar clean so a refresh does not replay the error.
    const url = new URL(window.location.href);
    ['error', 'error_code', 'error_description'].forEach((k) =>
      url.searchParams.delete(k)
    );
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  }, [enabled]);

  return message;
}

// ----------------------------------------------------------------------

// Long enough that a session restored in one quick round trip shows only the
// sky — a flash of words on every reload would be a flicker of its own —
// short enough that a slow answer, or a return from Microsoft (two round
// trips), is named while it is still being waited for.
const PENDING_DELAY_MS = 400;

PendingLine.propTypes = { label: PropTypes.string.isRequired };

/**
 * The one line that holds the card's place. A state, not an animation (§11):
 * it appears after the delay with a `standard` fade and nothing moves; the
 * sky's own motion is the life on the screen.
 */
function PendingLine({ label }) {
  return (
    <Typography
      role="status"
      aria-live="polite"
      sx={{
        textAlign: 'center',
        fontFamily: UNIVERSE_FONT,
        fontSize: 15,
        fontWeight: 500,
        lineHeight: 1.5,
        color: alpha('#FFFFFF', 0.82),
        // the halo the portal switch wears: on a short window this line can
        // land on the planet's lit limb
        textShadow: `0 0 6px ${alpha(UNIVERSE.night, 0.9)}, 0 1px 2px ${alpha(
          UNIVERSE.night,
          0.8
        )}`,
        '@keyframes authPendingAppear': {
          from: { opacity: 0 },
          to: { opacity: 1 }
        },
        animation: `authPendingAppear ${MOTION.standard}ms cubic-bezier(.2, 0, 0, 1) ${PENDING_DELAY_MS}ms both`
      }}
    >
      {label}
    </Typography>
  );
}

// ----------------------------------------------------------------------

PortalSwitch.propTypes = { portal: PropTypes.object.isRequired };

/**
 * The other two doors, under the card. Three portals share one sign-in design
 * and one error ("those credentials belong to the Clients portal"), so the
 * cheapest fix for arriving at the wrong one is to say where the others are
 * before the password is typed, not after it fails.
 */
function PortalSwitch({ portal }) {
  const others = AUTH_PORTAL_LIST.filter((p) => p.key !== portal.key);
  if (!others.length) return null;
  return (
    <Stack
      direction="row"
      spacing={0.75}
      justifyContent="center"
      alignItems="center"
      sx={{
        mt: 3,
        [AUTH_SHORTER]: { mt: 1.5 },
        flexWrap: 'wrap',
        // on a short window this line lands on the planet's lit limb, where
        // a pale word on pale blue disappears; a soft halo of the night
        // under it keeps it legible there and is invisible on the sky
        textShadow: `0 0 6px ${alpha(UNIVERSE.night, 0.9)}, 0 1px 2px ${alpha(
          UNIVERSE.night,
          0.8
        )}`
      }}
    >
      <Typography
        component="span"
        sx={{
          fontFamily: UNIVERSE_FONT,
          fontSize: 12.5,
          fontWeight: 400,
          color: alpha('#FFFFFF', 0.55)
        }}
      >
        Not your portal?
      </Typography>
      {others.map((p, i) => (
        <Stack key={p.key} direction="row" spacing={0.75} alignItems="center">
          {i > 0 && (
            <Box
              aria-hidden
              sx={{
                width: 3,
                height: 3,
                borderRadius: '50%',
                bgcolor: alpha('#FFFFFF', 0.3)
              }}
            />
          )}
          <Link
            component={RouterLink}
            to={p.path}
            underline="hover"
            sx={{
              fontFamily: UNIVERSE_FONT,
              fontSize: 12.5,
              fontWeight: 600,
              color: UNIVERSE.light,
              borderRadius: '4px',
              '&:hover': { color: '#FFFFFF' },
              '&:focus-visible': nightFocusSx
            }}
          >
            {p.label}
          </Link>
        </Stack>
      ))}
    </Stack>
  );
}

// ----------------------------------------------------------------------

export default function AuthPortalLayout({
  portal,
  title,
  subtitle,
  backTo,
  backLabel = 'Back to sign in',
  contentWidth,
  pending,
  children
}) {
  const emailLinkError = useEmailLinkError(!pending);

  return (
    <Page title={`${portal.label} · ${title || 'Sign in'}`}>
      {/* The UI face reaches the form's layers too — the password helper's
          tooltip, the terms dialog — until Phase 0 moves it into the theme. */}
      <GlobalStyles styles={LAYER_FONT_STYLES} />

      <RootStyle>
        {/* The whole window. The copy it carries is a fuller line than the
            portal chooser's card; a portal that does not need one has no
            `heroBlurb`. */}
        <BrandBanner
          variant="scene"
          logo={<AuthPortalMenu />}
          title={portal.heroTitle}
          blurb={portal.heroBlurb || portal.blurb}
        />

        <Box
          sx={{
            ...SCENE_FRAME,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            // `min-height` and not `height`: a tall form (the registration
            // flow) grows the box rather than being centred out of the top
            // of the window.
            minHeight: '100vh',
            // Below lg the lock-up is directly above the card, so the top
            // pad has to clear it on the shortest window that happens on.
            // the TOP pad clears the lock-up; the foot only has to give the
            // legal line some air (2026-10-11: a phone's 72px of it pushed a
            // 360 × 740 window into a scroll)
            pt: { xs: 8, lg: 7 },
            pb: { xs: 4, lg: 7 },
            // only the TOP pad has to clear the lock-up; the foot needs none
            // beyond the legal line's own air
            [AUTH_SHORT]: { pt: { xs: 8, lg: 3 }, pb: { xs: 3, lg: 3 } },
            [AUTH_SHORTER]: { pt: { xs: 7.5, lg: 2 }, pb: { xs: 1.5, lg: 2 } },
            ...PAGE_FONT_SX
          }}
        >
          <ContentStyle width={contentWidth}>
            {pending ? (
              <PendingLine label={pending} />
            ) : (
              <>
                {/* The way back out stands OUTSIDE the card: it leaves this
                screen, and a control that leaves does not belong on the sheet
                the form is written on. */}
                {backTo && (
                  <Link
                    component={RouterLink}
                    to={backTo}
                    underline="none"
                    sx={{
                      alignSelf: 'flex-start',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 2,
                      fontFamily: UNIVERSE_FONT,
                      fontSize: 12.5,
                      fontWeight: 500,
                      lineHeight: 1.4,
                      color: alpha('#FFFFFF', 0.7),
                      borderRadius: '4px',
                      '&:hover': { color: '#FFFFFF' },
                      '&:focus-visible': nightFocusSx
                    }}
                  >
                    <Icon name="ArrowLeft" size="xs" />
                    {backLabel}
                  </Link>
                )}

                <Box sx={cardSx}>
                  <Stack
                    spacing={4}
                    useFlexGap
                    sx={{
                      [AUTH_SHORT]: { gap: 3 },
                      [AUTH_SHORTER]: { gap: 2.5 }
                    }}
                  >
                    <Box>
                      {/* `page-title`, §3: 28 / 700, one title per screen. The
                      decorative gradient keyline that used to sit under it is
                      gone — §2 names the five jobs the gradient has, and
                      "a rule under a heading" is not one of them. */}
                      <Typography
                        component="h1"
                        sx={{
                          fontFamily: UNIVERSE_FONT,
                          fontSize: { xs: 24, md: 28 },
                          fontWeight: 700,
                          lineHeight: 1.2,
                          letterSpacing: '-0.01em',
                          color: 'text.primary'
                        }}
                      >
                        {title}
                      </Typography>

                      {subtitle && (
                        <Typography
                          sx={{
                            mt: 1.25,
                            fontFamily: UNIVERSE_FONT,
                            fontSize: 14,
                            fontWeight: 400,
                            lineHeight: 1.5,
                            color: 'text.secondary',
                            // two even lines, never a last word on its own
                            textWrap: 'balance'
                          }}
                        >
                          {subtitle}
                        </Typography>
                      )}
                    </Box>

                    {emailLinkError && (
                      <Alert severity="warning">{emailLinkError}</Alert>
                    )}

                    <Box>{children}</Box>
                  </Stack>
                </Box>

                <PortalSwitch portal={portal} />

                {/* THE LEGAL LINE below 1200 (the owner, 2026-10-11: on every
                sign-in screen). From 1200 up the sky's copy column carries it
                at the foot of the frame; below, that column stands down, so
                it comes here — in the flow under the card, where it can never
                sit beneath it. */}
                <LegalLine
                  align="center"
                  sx={{
                    display: { xs: 'block', lg: 'none' },
                    mt: { xs: 2.5, sm: 3 },
                    mx: 'auto',
                    maxWidth: 440,
                    // a phone gets it in two lines rather than three
                    fontSize: { xs: 10.5, sm: 11 },
                    letterSpacing: { xs: '0.05em', sm: '0.1em' },
                    [AUTH_SHORTER]: { mt: 1.5 }
                  }}
                />
              </>
            )}
          </ContentStyle>
        </Box>
      </RootStyle>
    </Page>
  );
}
